import { useCallback, useEffect, useState } from 'react';
import type { ShopItem, ShopItemType } from '@flip7/shared';
import { useAuth } from '../contexts/AuthContext';
import { Spinner } from '../components/Spinner';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface ShopItemWithOwned extends ShopItem {
  owned: boolean;
}

interface ShopScreenProps {
  onBack: () => void;
}

const CATEGORIES: { type: ShopItemType; label: string; icon: string; description: string }[] = [
  { type: 'title', label: 'Titles', icon: '\uD83C\uDFC6', description: 'Show off your status' },
  { type: 'avatar', label: 'Avatars', icon: '\uD83C\uDFAD', description: 'Express yourself' },
  { type: 'theme', label: 'Themes', icon: '\uD83C\uDFA8', description: 'Customize your look' },
  { type: 'soundpack', label: 'Sound Packs', icon: '\uD83C\uDFB5', description: 'Change up the vibes' },
  { type: 'soundbite', label: 'Sound Bites', icon: '\uD83D\uDD0A', description: 'Play sounds in-game' },
  { type: 'soundbitepack', label: 'Soundbite Packs', icon: '\uD83C\uDFAC', description: 'Unlock a full set of soundbites' },
];

const TYPE_COLORS: Record<ShopItemType, string> = {
  title: 'var(--gold-dim)',
  theme: 'var(--maroon)',
  soundpack: 'var(--color-freeze)',
  soundbite: '#6B7280',
  soundbitepack: '#7C3AED',
  avatar: '#D97706',
};

export function ShopScreen({ onBack }: ShopScreenProps) {
  const { accessToken, user, refreshUser, updateTitle } = useAuth();
  const [items, setItems] = useState<ShopItemWithOwned[]>([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ShopItemType | null>(null);

  const fetchShop = useCallback(async () => {
    if (!accessToken) return;
    const res = await fetch(`${API_BASE}/api/shop/items`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (res.ok) {
      const data = await res.json();
      setItems(data.items);
      setBalance(data.balance);
    }
    setLoading(false);
  }, [accessToken]);

  useEffect(() => { fetchShop(); }, [fetchShop]);

  const handleBuy = async (itemKey: string) => {
    if (!accessToken || buying) return;
    setBuying(itemKey);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/shop/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ itemKey }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Purchase failed' }));
        setError(err.message || 'Purchase failed');
      } else {
        const data = await res.json();
        setBalance(data.balance);
        setItems(prev => prev.map(i => i.key === itemKey ? { ...i, owned: true } : i));
        refreshUser();
      }
    } catch {
      setError('Purchase failed');
    } finally {
      setBuying(null);
    }
  };

  const handleEquip = async (itemKey: string | null, type: 'theme' | 'soundpack' | 'avatar') => {
    if (!accessToken) return;
    await fetch(`${API_BASE}/api/shop/equip`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ itemKey, type }),
    });
    refreshUser();
  };

  if (loading) {
    return (
      <div className="hero-panel">
        <div className="screen screen-center">
          <Spinner small />
        </div>
      </div>
    );
  }

  // Category listing view
  if (!selectedCategory) {
    return (
      <div className="hero-panel">
        <div className="screen screen--padded-top content-narrow" style={{ padding: 'var(--space-xl) var(--space-lg)' }}>
          <div className="page-header" style={{ marginBottom: 'var(--space-xl)' }}>
            <button onClick={onBack} className="back-btn">&larr;</button>
            <h1 className="section-header" style={{ flex: 1, fontSize: '1.6rem' }}>Shop</h1>
            <div className="coin-badge">{balance}</div>
          </div>

          <div className="flex-col" style={{ gap: '10px' }}>
            {CATEGORIES.map(cat => {
              const catItems = items.filter(i => i.type === cat.type);
              const ownedCount = catItems.filter(i => i.owned).length;
              const unownedCount = catItems.length - ownedCount;
              return (
                <button
                  key={cat.type}
                  onClick={() => setSelectedCategory(cat.type)}
                  className="shop-category"
                >
                  <span className="shop-category__icon">{cat.icon}</span>
                  <div className="shop-category__info">
                    <div className="shop-category__name">{cat.label}</div>
                    <div className="shop-category__desc">{cat.description}</div>
                  </div>
                  <div className="shop-category__count">
                    {unownedCount > 0 ? (
                      <span className="shop-category__new">{unownedCount} new</span>
                    ) : (
                      <span className="shop-category__all-owned">All owned</span>
                    )}
                    <div className="shop-category__progress">{ownedCount}/{catItems.length}</div>
                  </div>
                  <span className="shop-category__arrow">&rsaquo;</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Category detail view
  const category = CATEGORIES.find(c => c.type === selectedCategory)!;
  const categoryItems = items.filter(i => i.type === selectedCategory);
  const unowned = categoryItems.filter(i => !i.owned);
  const owned = categoryItems.filter(i => i.owned);

  return (
    <div className="hero-panel">
      <div className="screen screen--padded-top content-narrow" style={{ padding: 'var(--space-xl) var(--space-lg)' }}>
        <div className="page-header" style={{ marginBottom: 'var(--space-lg)' }}>
          <button onClick={() => setSelectedCategory(null)} className="back-btn">&larr;</button>
          <h1 className="section-header" style={{ flex: 1 }}>
            {category.icon} {category.label}
          </h1>
          <div className="coin-badge">{balance}</div>
        </div>

        {error && <div className="shop-error">{error}</div>}

        {unowned.length > 0 && (
          <div style={{ marginBottom: 'var(--space-xl)' }}>
            <p className="label" style={{ marginBottom: '10px' }}>Available</p>
            <div className="flex-col" style={{ gap: '8px' }}>
              {unowned.map(item => (
                <ShopItemCard
                  key={item.key}
                  item={item}
                  user={user}
                  balance={balance}
                  buying={buying}
                  onBuy={handleBuy}
                  onEquip={handleEquip}
                  onUpdateTitle={updateTitle}
                />
              ))}
            </div>
          </div>
        )}

        {owned.length > 0 && (
          <div>
            <p className="label" style={{ marginBottom: '10px' }}>Owned ({owned.length})</p>
            <div className="flex-col" style={{ gap: '8px' }}>
              {owned.map(item => (
                <ShopItemCard
                  key={item.key}
                  item={item}
                  user={user}
                  balance={balance}
                  buying={buying}
                  onBuy={handleBuy}
                  onEquip={handleEquip}
                  onUpdateTitle={updateTitle}
                />
              ))}
            </div>
          </div>
        )}

        {categoryItems.length === 0 && (
          <div className="text-center" style={{ padding: '40px 20px' }}>
            <p className="text-center-muted">No items in this category yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ShopItemCard({ item, user, balance, buying, onBuy, onEquip, onUpdateTitle }: {
  item: ShopItemWithOwned;
  user: ReturnType<typeof useAuth>['user'];
  balance: number;
  buying: string | null;
  onBuy: (key: string) => void;
  onEquip: (key: string | null, type: 'theme' | 'soundpack' | 'avatar') => void;
  onUpdateTitle: (title: string | null) => void;
}) {
  const isEquipped = item.type === 'title'
    ? user?.activeTitle === item.titleText
    : item.type === 'theme'
      ? user?.activeTheme === item.key
      : item.type === 'avatar'
        ? user?.activeAvatar === item.icon
        : user?.activeSoundpack === item.key;

  const handleToggle = () => {
    if (item.type === 'title') {
      onUpdateTitle(isEquipped ? null : item.titleText ?? null);
    } else {
      onEquip(isEquipped ? null : item.key, item.type as 'theme' | 'soundpack' | 'avatar');
    }
  };

  return (
    <div className={`shop-item${isEquipped ? ' shop-item--equipped' : ''}`}>
      <div className="shop-item__row">
        <span className="shop-item__icon">{item.icon}</span>
        <div className="shop-item__info">
          <div className="shop-item__name-row">
            <span className="shop-item__name">{item.name}</span>
            {isEquipped && <span className="shop-item__active-badge">Active</span>}
          </div>
          <p className="shop-item__desc">{item.description}</p>
          {item.preview && (
            <p className="shop-item__preview" style={{ color: TYPE_COLORS[item.type] ?? 'var(--text-muted)' }}>
              {item.preview}
            </p>
          )}
        </div>
        <div>
          {item.owned ? (
            item.type === 'soundbite' ? (
              <span className="shop-item__owned-label">Owned</span>
            ) : (
              <button onClick={handleToggle} className={`shop-item__equip-btn${isEquipped ? ' shop-item__equip-btn--active' : ''}`}>
                {isEquipped ? 'Unequip' : 'Equip'}
              </button>
            )
          ) : (
            <button
              onClick={() => onBuy(item.key)}
              disabled={buying === item.key || balance < item.price}
              className={`shop-item__buy-btn${balance >= item.price ? '' : ' shop-item__buy-btn--disabled'}`}
              style={{ opacity: buying === item.key ? 0.6 : 1 }}
            >
              {buying === item.key ? '...' : `${item.price}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
